import React from "react";
import { Step4Props } from "../../../../../redux/AllocationRequest";
import "./index.css";

export const Step4: React.FC<Step4Props> = ({
  type,
  details,
  items,
  cartAllocations,
  bulkGridData,
  bulkProducts,
  bulkCarts,
  bulkCustomData,
}: any) => {
  const getTierClass = (tier: string) => {
    const tierLower = tier?.toLowerCase();
    switch (tierLower) {
      case "platinum":
        return "step4-tier-platinum";
      case "gold":
        return "step4-tier-gold";
      case "silver":
        return "step4-tier-silver";
      case "bronze":
        return "step4-tier-bronze";
      default:
        return "step4-tier-default";
    }
  };

  const getTierIcon = (tier: string): string => {
    switch (tier?.toLowerCase()) {
      case "platinum":
        return "💎";
      case "gold":
        return "🥇";
      case "silver":
        return "🥈";
      case "bronze":
        return "🥉";
      default:
        return "";
    }
  };

  const TIER_COLORS: Record<
    string,
    { bg: string; tagBg: string; color: string }
  > = {
    Platinum: { bg: "#F1F5F9", tagBg: "#64748B", color: "#fff" },
    Gold: { bg: "#FFFBEB", tagBg: "#B45309", color: "#fff" },
    Silver: { bg: "#F8FAFC", tagBg: "#475569", color: "#fff" },
    Bronze: { bg: "#FFF7ED", tagBg: "#9A3412", color: "#fff" },
    Value: { bg: "#F0FDF4", tagBg: "#166534", color: "#fff" },
    Staff: { bg: "#EFF6FF", tagBg: "#1D4ED8", color: "#fff" },
  };

  const formatChannelName = (channel: string): string => {
    switch (channel) {
      case "WebPickup":
        return "Web Pickup";
      case "WebDelivery":
        return "Web Delivery";
      case "WhatsApp":
        return "WhatsApp";
      default:
        return channel;
    }
  };

  const totalProducts = cartAllocations
    ? cartAllocations.reduce((s: number, c: any) => s + c.allocations.length, 0)
    : items.length;
  const totalQuantity = cartAllocations
    ? cartAllocations.reduce((s: number, c: any) => s + c.totalQuantity, 0)
    : items.reduce((s: number, i: any) => s + i.qty, 0);
  const totalAmount = cartAllocations
    ? cartAllocations.reduce((s: number, c: any) => s + c.totalAmount, 0)
    : items.reduce(
        (s: number, i: any) => s + (i.overridePrice || i.price || 0) * i.qty,
        0,
      );

  // ─── Bulk summary helpers ─────────────────────────────────────────────────
  const getBulkRowTotal = (productId: string): number => {
    if (!bulkGridData || !bulkCarts) return 0;
    return (bulkCarts as any[]).reduce(
      (sum: number, cart: any) =>
        sum + (bulkGridData[cart.cartId]?.[productId] || 0),
      0,
    );
  };

  const getBulkCartTotal = (cartId: string): number => {
    if (!bulkGridData || !bulkProducts) return 0;
    return (bulkProducts as any[]).reduce(
      (sum: number, p: any) => sum + (bulkGridData[cartId]?.[p.itemId] || 0),
      0,
    );
  };

  const bulkGrandTotal = bulkProducts
    ? (bulkProducts as any[]).reduce(
        (sum: number, p: any) => sum + getBulkRowTotal(p.itemId),
        0,
      )
    : 0;

  // Group products by tier for summary
  const tierSummary: Record<string, number> = {};
  if (bulkProducts && bulkGridData) {
    (bulkProducts as any[]).forEach((p: any) => {
      const qty = getBulkRowTotal(p.itemId);
      if (qty > 0) {
        tierSummary[p.tier] = (tierSummary[p.tier] || 0) + qty;
      }
    });
  }

  // ─── Custom menu items breakdown for SKU view ────────────────────────────
  const getCustomMenuItemsBreakdown = () => {
    const customItemsMap: Map<
      string,
      {
        displayName: string;
        skuId: string;
        quantity: number;
        parentProductName: string;
        parentProductTier: string;
        unitPrice: number;
      }
    > = new Map();

    if (!bulkProducts || !bulkGridData || !bulkCustomData || !bulkCarts)
      return [];

    bulkProducts.forEach((product: any) => {
      if (product.itemType === "CustomMenu") {
        const customConfig = bulkCustomData[product.itemId];
        if (customConfig?.customItems?.length) {
          // Calculate total quantity of this custom menu across all carts
          let totalProductQty = 0;
          bulkCarts?.forEach((cart: any) => {
            totalProductQty += bulkGridData[cart.cartId]?.[product.itemId] || 0;
          });

          if (totalProductQty > 0) {
            // For each custom item in the configuration, multiply by the product quantity
            customConfig.customItems.forEach((customItem: any) => {
              const itemQty = (customItem.quantity || 1) * totalProductQty;
              const key = `${customItem.menuItemId || customItem.itemId}_${customItem.skuId || ""}`;

              if (customItemsMap.has(key)) {
                const existing = customItemsMap.get(key)!;
                existing.quantity += itemQty;
              } else {
                customItemsMap.set(key, {
                  displayName: customItem.displayName,
                  skuId: customItem.skuId || "",
                  quantity: itemQty,
                  parentProductName: product.displayName,
                  parentProductTier: product.tier || "",
                  unitPrice: customItem.unitPrice || 0,
                });
              }
            });
          }
        }
      }
    });

    return Array.from(customItemsMap.values()).sort(
      (a, b) => b.quantity - a.quantity,
    );
  };

  // ─── Request details card (shared) ───────────────────────────────────────
  const RequestDetailsCard = () => (
    <div className="step4-details-card">
      <div className="step4-details-grid">
        <div className="step4-detail-item">
          <div className="step4-detail-label">Request Type</div>
          <div className="step4-detail-value">
            <span className="step4-badge-request">{type}</span>
          </div>
        </div>
        <div className="step4-detail-item">
          <div className="step4-detail-label">Department</div>
          <div className="step4-detail-value">{details.department || "—"}</div>
        </div>
        <div className="step4-detail-item">
          <div className="step4-detail-label">Requested By</div>
          <div className="step4-detail-value">{details.requestedBy || "—"}</div>
        </div>
        <div className="step4-detail-item">
          <div className="step4-detail-label">Target Date</div>
          <div className="step4-detail-value">{details.targetDate || "—"}</div>
        </div>
        {details.notes && (
          <div className="step4-detail-item step4-detail-full">
            <div className="step4-detail-label">Notes</div>
            <div className="step4-detail-value step4-notes">
              {details.notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── BULK CART ALLOCATION SUMMARY ─────────────────────────────────────────
  if (type === "Bulk Cart Allocation") {
    const hasData =
      bulkProducts && bulkCarts && bulkGridData && bulkGrandTotal > 0;
    const customItemsBreakdown = getCustomMenuItemsBreakdown();

    return (
      <div className="step4-container">
        <div className="step4-header">
          <h3 className="step4-title">Review Your Request</h3>
          <div className="step4-subtitle">
            Please verify all details before submitting
          </div>
        </div>

        <RequestDetailsCard />

        {hasData ? (
          <>
            {/* Tier + Product summary row */}
            <div className="bulk-summary-two-col">
              {/* By product */}
              <div className="bulk-summary-card">
                <div className="bulk-summary-card-hd">
                  Packs by Product / SKU
                </div>
                <table className="bulk-summary-table">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bulkProducts as any[])
                      .filter((p: any) => getBulkRowTotal(p.itemId) > 0) // Only show items with quantity > 0
                      .map((p: any) => {
                        const qty = getBulkRowTotal(p.itemId);
                        const tc = TIER_COLORS[p.tier] || {
                          bg: "#F8FAFC",
                          tagBg: "#374151",
                          color: "#fff",
                        };
                        return (
                          <tr
                            key={p.itemId}
                            style={{ background: qty > 0 ? tc.bg : undefined }}
                          >
                            <td>
                              <span
                                className="bulk-summary-tier-tag"
                                style={{
                                  background: tc.tagBg,
                                  color: tc.color,
                                }}
                              >
                                {getTierIcon(p.tier)} {p.tier}
                              </span>
                            </td>
                            <td
                              style={{
                                fontWeight: qty > 0 ? 600 : 400,
                                color: qty === 0 ? "#9ca3af" : undefined,
                              }}
                            >
                              {p.displayName}
                            </td>
                            <td
                              style={{
                                fontFamily: "monospace",
                                fontSize: 11,
                                color: "#94a3b8",
                              }}
                            >
                              {p.skuId || "—"}
                            </td>
                            <td
                              className={`bulk-summary-num ${qty === 0 ? "bulk-summary-num-zero" : ""}`}
                            >
                              {qty}
                            </td>
                          </tr>
                        );
                      })}
                    {bulkProducts &&
                      (bulkProducts as any[]).filter(
                        (p: any) => getBulkRowTotal(p.itemId) > 0,
                      ).length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              color: "#9ca3af",
                              textAlign: "center",
                              padding: "16px",
                            }}
                          >
                            No products with quantity assigned
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              {/* By tier */}
              <div className="bulk-summary-card">
                <div className="bulk-summary-card-hd">Packs by Tier</div>
                <table className="bulk-summary-table">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th style={{ textAlign: "right" }}>Packs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(tierSummary)
                      .sort(([, a], [, b]) => b - a)
                      .map(([tier, qty]) => {
                        const tc = TIER_COLORS[tier] || {
                          bg: "#F8FAFC",
                          tagBg: "#374151",
                          color: "#fff",
                        };
                        return (
                          <tr key={tier} style={{ background: tc.bg }}>
                            <td>
                              <span
                                className="bulk-summary-tier-tag"
                                style={{
                                  background: tc.tagBg,
                                  color: tc.color,
                                }}
                              >
                                {getTierIcon(tier)} {tier}
                              </span>
                            </td>
                            <td className="bulk-summary-num">{qty}</td>
                          </tr>
                        );
                      })}
                    {Object.keys(tierSummary).length === 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            color: "#9ca3af",
                            textAlign: "center",
                            padding: "16px",
                          }}
                        >
                          No quantities entered
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Per-cart mini cards */}
                <div className="bulk-summary-card-hd" style={{ marginTop: 16 }}>
                  Packs per Cart
                </div>
                <div className="bulk-summary-cart-grid">
                  {(bulkCarts as any[]).map((cart: any) => {
                    const cartTotal = getBulkCartTotal(cart.cartId);
                    return (
                      <div key={cart.cartId} className="bulk-summary-cart-card">
                        <div className="bulk-summary-cart-name">
                          {cart.displayName}
                        </div>
                        <div
                          className={`bulk-summary-cart-num ${cartTotal === 0 ? "bulk-summary-cart-num-zero" : ""}`}
                        >
                          {cartTotal}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Packs by SKU / Custom Menu Items Breakdown */}
                {customItemsBreakdown.length > 0 && (
                  <>
                    <div
                      className="bulk-summary-card-hd"
                      style={{ marginTop: 16 }}
                    >
                      Packs by SKU (Custom Menu Items)
                    </div>
                    <div className="bulk-summary-sku-grid">
                      <table className="bulk-summary-table">
                        <thead>
                          <tr>
                            <th>SKU / Item</th>
                            <th>Parent Product</th>
                            <th>Tier</th>
                            <th style={{ textAlign: "right" }}>
                              Total Quantity
                            </th>
                            <th style={{ textAlign: "right" }}>Unit Price</th>
                            <th style={{ textAlign: "right" }}>Total Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customItemsBreakdown.map((item, idx) => {
                            const tc = TIER_COLORS[item.parentProductTier] || {
                              bg: "#F8FAFC",
                              tagBg: "#374151",
                              color: "#fff",
                            };
                            const totalValue = item.quantity * item.unitPrice;
                            return (
                              <tr key={idx} style={{ background: tc.bg }}>
                                <td>
                                  <div className="bulk-summary-sku-info">
                                    <span className="bulk-summary-sku-name">
                                      {item.displayName}
                                    </span>
                                  </div>
                                </td>
                                <td style={{ fontSize: 12, color: "#6b7280" }}>
                                  {item.parentProductName}
                                </td>
                                <td>
                                  <span
                                    className="bulk-summary-tier-tag"
                                    style={{
                                      background: tc.tagBg,
                                      color: tc.color,
                                    }}
                                  >
                                    {getTierIcon(item.parentProductTier)}{" "}
                                    {item.parentProductTier}
                                  </span>
                                </td>
                                <td className="bulk-summary-num">
                                  {item.quantity}
                                </td>
                                <td
                                  className="bulk-summary-num"
                                  style={{ fontSize: 12 }}
                                >
                                  Rs. {item.unitPrice.toFixed(2)}
                                </td>
                                <td
                                  className="bulk-summary-num"
                                  style={{ fontWeight: 600 }}
                                >
                                  Rs. {totalValue.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                            <td
                              colSpan={5}
                              style={{ textAlign: "right", fontWeight: 600 }}
                            >
                              Total Value:
                            </td>
                            <td
                              className="bulk-summary-num"
                              style={{ fontWeight: 700, color: "#059669" }}
                            >
                              Rs.{" "}
                              {customItemsBreakdown
                                .reduce(
                                  (sum, item) =>
                                    sum + item.quantity * item.unitPrice,
                                  0,
                                )
                                .toFixed(2)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="step4-empty-state">
            <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
            <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              No quantities entered yet
            </div>
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              Go back to Step 3 and enter quantities in the grid.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── CART ALLOCATION SUMMARY ──────────────────────────────────────────────
  if (
    type === "Cart Allocation" &&
    cartAllocations &&
    cartAllocations.length > 0
  ) {
    return (
      <div className="step4-container">
        <div className="step4-header">
          <h3 className="step4-title">Review Your Request</h3>
          <div className="step4-subtitle">
            Please verify all details before submitting
          </div>
        </div>
        <RequestDetailsCard />
        <div className="step4-allocations-section">
          <div className="step4-section-header">
            <div className="step4-section-title">
              Cart Allocations{" "}
              <span className="step4-section-count">
                {cartAllocations.length} carts
              </span>
            </div>
            <div className="step4-summary-stats">
              <div className="step4-stat">
                <span className="step4-stat-label">Total Products:</span>
                <span className="step4-stat-value">{totalProducts}</span>
              </div>
              <div className="step4-stat">
                <span className="step4-stat-label">Total Quantity:</span>
                <span className="step4-stat-value">{totalQuantity}</span>
              </div>
              <div className="step4-stat">
                <span className="step4-stat-label">Total Amount:</span>
                <span className="step4-stat-value step4-amount">
                  Rs. {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {cartAllocations.map((cart: any) => (
            <div key={cart.cartId} className="step4-cart-card">
              <div className="step4-cart-header">
                <div className="step4-cart-info">
                  <div className="step4-cart-name">{cart.cartName}</div>
                  <div className="step4-cart-id">ID: {cart.cartId}</div>
                </div>
                <div className="step4-cart-total">
                  <div className="step4-cart-total-amount">
                    Rs. {cart.totalAmount.toFixed(2)}
                  </div>
                  <div className="step4-cart-total-qty">
                    {cart.totalQuantity} items total
                  </div>
                </div>
              </div>

              <div className="step4-products-list">
                {cart.allocations.map((product: any) => {
                  const productPrice = product.price || 0;
                  const productTotal =
                    product.subtotal || productPrice * product.quantity;
                  const isCustom = product.itemType === "CustomMenu";
                  return (
                    <div key={product.itemId} className="step4-product-row">
                      <div className="step4-product-main">
                        <div className="step4-product-badge">
                          <span
                            className={`step4-tier-badge ${getTierClass(product.tier)}`}
                          >
                            {getTierIcon(product.tier)}{" "}
                            {product.tier || "No tier"}
                          </span>
                          {isCustom && (
                            <span className="step4-custom-badge">Custom</span>
                          )}
                        </div>
                        <div className="step4-product-name">
                          {product.productName}
                          {product.skuId && (
                            <span className="step4-product-sku">
                              SKU: {product.skuId}
                            </span>
                          )}
                        </div>
                        <div className="step4-product-price-info">
                          <span className="step4-unit-price">
                            Rs. {productPrice.toFixed(2)}
                          </span>
                          <span className="step4-price-separator">×</span>
                          <span className="step4-product-quantity">
                            {product.quantity}
                          </span>
                          <span className="step4-price-separator">=</span>
                          <span className="step4-product-total">
                            Rs. {productTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {Object.keys(product.channels).some(
                        (ch: string) => product.channels[ch] > 0,
                      ) && (
                        <div className="step4-channel-section">
                          <div className="step4-channel-label">
                            Channel Distribution:
                          </div>
                          <div className="step4-channel-list">
                            {Object.entries(product.channels).map(
                              ([channel, qty]: any) =>
                                qty > 0 && (
                                  <div
                                    key={channel}
                                    className="step4-channel-item"
                                  >
                                    <span className="step4-channel-name">
                                      {formatChannelName(channel)}
                                    </span>
                                    <span className="step4-channel-qty">
                                      {qty}
                                    </span>
                                  </div>
                                ),
                            )}
                          </div>
                        </div>
                      )}
                      {isCustom &&
                        product.customItems &&
                        product.customItems.length > 0 && (
                          <div className="step4-custom-items-section">
                            <div className="step4-custom-items-label">
                              Customization:
                            </div>
                            <div className="step4-custom-items-list">
                              {product.customItems.map(
                                (customItem: any, idx: number) => (
                                  <div key={idx} className="step4-custom-item">
                                    <span className="step4-custom-item-name">
                                      {customItem.displayName}
                                    </span>
                                    <span className="step4-custom-item-qty">
                                      ×{customItem.quantity || 1}
                                    </span>
                                    <span className="step4-custom-item-price">
                                      Rs.{" "}
                                      {(
                                        customItem.unitPrice *
                                        (customItem.quantity || 1)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>

              <div className="step4-cart-footer">
                <div className="step4-cart-summary">
                  <span className="step4-summary-text">Cart Summary:</span>
                  <span className="step4-summary-items">
                    {cart.allocations.length} products
                  </span>
                  <span className="step4-summary-separator">•</span>
                  <span className="step4-summary-qty">
                    {cart.totalQuantity} items
                  </span>
                  <span className="step4-summary-separator">•</span>
                  <span className="step4-summary-amount">
                    Rs. {cart.totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── REGULAR ITEMS SUMMARY ────────────────────────────────────────────────
  return (
    <div className="step4-container">
      <div className="step4-header">
        <h3 className="step4-title">Review Your Request</h3>
        <div className="step4-subtitle">
          Please verify all details before submitting
        </div>
      </div>
      <RequestDetailsCard />
      {items.length > 0 ? (
        <div className="step4-regular-items-section">
          <div className="step4-section-header">
            <div className="step4-section-title">
              Products{" "}
              <span className="step4-section-count">{items.length} items</span>
            </div>
            <div className="step4-summary-stats">
              <div className="step4-stat">
                <span className="step4-stat-label">Total Quantity:</span>
                <span className="step4-stat-value">{totalQuantity}</span>
              </div>
              <div className="step4-stat">
                <span className="step4-stat-label">Total Amount:</span>
                <span className="step4-stat-value step4-amount">
                  Rs. {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <div className="step4-regular-items-list">
            {items.map((item: any) => {
              const price = item.overridePrice || item.price || 0;
              const total = price * item.qty;
              const isCustom = item.itemType === "CustomMenu";
              return (
                <div key={item.id} className="step4-regular-item">
                  <div className="step4-regular-item-main">
                    <div className="step4-regular-item-badge">
                      <span
                        className={`step4-tier-badge ${getTierClass(item.tier)}`}
                      >
                        {getTierIcon(item.tier)} {item.tier || "No tier"}
                      </span>
                      {isCustom && (
                        <span className="step4-custom-badge">Custom</span>
                      )}
                    </div>
                    <div className="step4-regular-item-name">
                      {item.name}
                      {item.skuId && (
                        <span className="step4-product-sku">
                          SKU: {item.skuId}
                        </span>
                      )}
                    </div>
                    <div className="step4-regular-item-price-info">
                      <span className="step4-unit-price">
                        Rs. {price.toFixed(2)}
                      </span>
                      <span className="step4-price-separator">×</span>
                      <span className="step4-item-quantity">{item.qty}</span>
                      <span className="step4-price-separator">=</span>
                      <span className="step4-item-total">
                        Rs. {total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {isCustom &&
                    (item as any).customItems &&
                    (item as any).customItems.length > 0 && (
                      <div className="step4-custom-items-section">
                        <div className="step4-custom-items-label">
                          Customization:
                        </div>
                        <div className="step4-custom-items-list">
                          {(item as any).customItems.map(
                            (customItem: any, idx: number) => (
                              <div key={idx} className="step4-custom-item">
                                <span className="step4-custom-item-name">
                                  {customItem.displayName}
                                </span>
                                <span className="step4-custom-item-qty">
                                  ×{customItem.quantity || 1}
                                </span>
                                <span className="step4-custom-item-price">
                                  Rs.{" "}
                                  {(
                                    customItem.unitPrice *
                                    (customItem.quantity || 1)
                                  ).toFixed(2)}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
